// 19 §1.2: לוח הזמנים של טריגר — cron של 5 שדות (דקה שעה יום חודש יום-בשבוע) ב-Asia/Jerusalem עם DST נכון
// (מועד שלא קיים — מדלגים למועד הבא; מועד כפול — פעם אחת, המוקדם), interval בדקות, המרווח המינימלי (too_frequent),
// ו-describeHe ("כל יום ב-07:00"). טהור — בלי מסד ובלי שעון: "עכשיו" תמיד מגיע מהקורא.
import { TRIGGER_MIN_MINUTES, TRIGGER_TZ, type TriggerSchedule } from './triggers.ts';

export type CronSpec = {
  minutes: number[];
  hours: number[];
  dom: Set<number>;
  months: Set<number>;
  dow: Set<number>;
  domStar: boolean;
  dowStar: boolean;
};

export class CronError extends Error {}

const FIELDS: Array<[number, number, string]> = [
  [0, 59, 'דקה'],
  [0, 23, 'שעה'],
  [1, 31, 'יום בחודש'],
  [1, 12, 'חודש'],
  [0, 7, 'יום בשבוע'],
];

function field(src: string, i: number): { values: number[]; star: boolean } {
  const [lo, hi, nameHe] = FIELDS[i]!;
  const out = new Set<number>();
  let star = false;
  for (const part of src.split(',')) {
    const m = part.match(/^(\*|\d+(?:-\d+)?)(?:\/(\d+))?$/);
    if (!m) throw new CronError(`שדה ${nameHe} לא תקין: '${part}'`);
    const step = m[2] ? Number(m[2]) : 1;
    if (step < 1) throw new CronError(`צעד לא תקין בשדה ${nameHe}`);
    let a = lo;
    let b = hi;
    if (m[1] === '*') {
      if (!m[2]) star = true;
    } else {
      const [x, y] = m[1]!.split('-').map(Number);
      a = x!;
      b = y ?? (m[2] ? hi : x!);
    }
    if (a < lo || b > hi || a > b) throw new CronError(`ערך מחוץ לטווח בשדה ${nameHe} (${lo} עד ${hi})`);
    for (let v = a; v <= b; v += step) out.add(i === 4 && v === 7 ? 0 : v);
  }
  return { values: [...out].sort((x, y) => x - y), star };
}

export function parseCron(expr: string): CronSpec {
  const parts = expr.trim().split(/\s+/);
  if (parts.length !== 5) throw new CronError('ביטוי cron צריך 5 שדות: דקה שעה יום חודש יום-בשבוע');
  const [mi, h, dom, mo, dow] = parts.map((p, i) => field(p, i));
  return { minutes: mi!.values, hours: h!.values, dom: new Set(dom!.values), months: new Set(mo!.values), dow: new Set(dow!.values), domStar: dom!.star, dowStar: dow!.star };
}

// ───────────────────────────── זמן מקומי ─────────────────────────────

const fmt = new Intl.DateTimeFormat('en-US', { timeZone: TRIGGER_TZ, hourCycle: 'h23', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });
type Local = { y: number; mo: number; d: number; h: number; mi: number };

export function localParts(ms: number): Local {
  const p = Object.fromEntries(fmt.formatToParts(new Date(ms)).map((x) => [x.type, x.value]));
  return { y: Number(p.year), mo: Number(p.month), d: Number(p.day), h: Number(p.hour) % 24, mi: Number(p.minute) };
}
const offsetMin = (ms: number) => {
  const l = localParts(ms);
  return Math.round((Date.UTC(l.y, l.mo - 1, l.d, l.h, l.mi) - Math.floor(ms / 60_000) * 60_000) / 60_000);
};

/** שעון קיר מקומי → UTC. מועד כפול (חזרה משעון קיץ) → המוקדם; מועד שלא קיים (מעבר לשעון קיץ) → null. */
export function localToUtc(y: number, mo: number, d: number, h: number, mi: number): number | null {
  const naive = Date.UTC(y, mo - 1, d, h, mi);
  const cands = [...new Set([naive - offsetMin(naive - 12 * 3600_000) * 60_000, naive - offsetMin(naive + 12 * 3600_000) * 60_000])].sort((a, b) => a - b);
  for (const c of cands) {
    const l = localParts(c);
    if (l.y === y && l.mo === mo && l.d === d && l.h === h && l.mi === mi) return c;
  }
  return null;
}

const dayMatches = (s: CronSpec, y: number, mo: number, d: number) => {
  if (!s.months.has(mo)) return false;
  const wd = new Date(Date.UTC(y, mo - 1, d)).getUTCDay();
  const domOk = s.dom.has(d);
  const dowOk = s.dow.has(wd);
  // cron קלאסי: כששני השדות מוגבלים — או; אחרת — רק המוגבל
  if (!s.domStar && !s.dowStar) return domOk || dowOk;
  if (!s.domStar) return domOk;
  if (!s.dowStar) return dowOk;
  return true;
};

/** המועד הראשון אחרי afterMs (ממש אחרי). null = הביטוי לא מתקיים אף פעם (31 בפברואר). */
export function nextCron(s: CronSpec, afterMs: number): number | null {
  const start = localParts(afterMs);
  const base = Date.UTC(start.y, start.mo - 1, start.d);
  for (let k = 0; k < 366 * 8; k++) {
    const day = new Date(base + k * 86_400_000);
    const y = day.getUTCFullYear();
    const mo = day.getUTCMonth() + 1;
    const d = day.getUTCDate();
    if (!dayMatches(s, y, mo, d)) continue;
    for (const h of s.hours)
      for (const mi of s.minutes) {
        const t = localToUtc(y, mo, d, h, mi);
        if (t !== null && t > afterMs) return t;
      }
  }
  return null;
}

/** המרווח הקטן ביותר בדקות בין שתי הפעלות עוקבות (כולל מעבר ליום הבא — שמרני). */
export function minGapMinutes(s: CronSpec): number {
  const times = s.hours.flatMap((h) => s.minutes.map((m) => h * 60 + m)).sort((a, b) => a - b);
  if (!times.length) return Infinity;
  let gap = times[0]! + 1440 - times.at(-1)!;
  for (let i = 1; i < times.length; i++) gap = Math.min(gap, times[i]! - times[i - 1]!);
  return gap;
}

// ───────────────────────────── schedule ─────────────────────────────

/** בדיקת schedule לשמירה: שגיאה בעברית או null. */
export function validateSchedule(s: TriggerSchedule): { code: 'invalid_schedule' | 'too_frequent'; textHe: string } | null {
  if (s.kind === 'interval') {
    if (s.minutes < TRIGGER_MIN_MINUTES) return { code: 'too_frequent', textHe: `תזמון לכל היותר פעם ב-${TRIGGER_MIN_MINUTES} דקות` };
    return null;
  }
  let spec: CronSpec;
  try {
    spec = parseCron(s.expr);
  } catch (e) {
    return { code: 'invalid_schedule', textHe: e instanceof CronError ? e.message : 'ביטוי cron לא תקין' };
  }
  if (minGapMinutes(spec) < TRIGGER_MIN_MINUTES) return { code: 'too_frequent', textHe: `תזמון לכל היותר פעם ב-${TRIGGER_MIN_MINUTES} דקות (הביטוי '${s.expr}' מפעיל לעתים קרובות יותר)` };
  if (nextCron(spec, Date.now()) === null) return { code: 'invalid_schedule', textHe: `הביטוי '${s.expr}' לא מתקיים אף פעם` };
  return null;
}

/** המועד הבא אחרי afterMs. interval — afterMs + N דקות (afterMs = המועד הקודם, כדי שהשעון לא יזחל). */
export function nextRun(s: TriggerSchedule, afterMs: number): number | null {
  if (s.kind === 'interval') return afterMs + s.minutes * 60_000;
  return nextCron(parseCron(s.expr), afterMs);
}

// ───────────────────────────── describeHe ─────────────────────────────

const DAY_LETTER = ['א', 'ב', 'ג', 'ד', 'ה', 'ו', 'ש'];
const DAY_NAME = ['ראשון', 'שני', 'שלישי', 'רביעי', 'חמישי', 'שישי', 'שבת'];
const pad = (n: number) => String(n).padStart(2, '0');

/** "כל יום ב-07:00", "ימים א–ה ב-08:30", "ב-1 לחודש ב-06:00", "כל 30 דקות". */
export function describeHe(s: TriggerSchedule): string {
  if (s.kind === 'interval') {
    if (s.minutes % 1440 === 0) return s.minutes === 1440 ? 'כל יום' : `כל ${s.minutes / 1440} ימים`;
    if (s.minutes % 60 === 0) return s.minutes === 60 ? 'כל שעה' : `כל ${s.minutes / 60} שעות`;
    return `כל ${s.minutes} דקות`;
  }
  let c: CronSpec;
  try {
    c = parseCron(s.expr);
  } catch {
    return `לפי cron '${s.expr}'`;
  }
  const fallback = `לפי cron '${s.expr}'`;
  const allMonths = c.months.size === 12;
  // "*/N * * * *" / "M */N * * *"
  const parts = s.expr.trim().split(/\s+/);
  if (c.domStar && c.dowStar && allMonths && parts[1] === '*') {
    const every = parts[0]!.match(/^\*\/(\d+)$/);
    if (every) return `כל ${every[1]} דקות`;
    if (c.minutes.length === 1) return c.minutes[0] === 0 ? 'כל שעה עגולה' : `כל שעה, בדקה ${pad(c.minutes[0]!)}`;
    return fallback;
  }
  if (c.minutes.length !== 1 || !allMonths) return fallback;
  const times = c.hours.map((h) => `${pad(h)}:${pad(c.minutes[0]!)}`);
  const at = times.length === 1 ? `ב-${times[0]}` : times.length <= 4 ? `ב-${times.join(', ')}` : null;
  if (!at) return fallback;
  if (c.domStar && c.dowStar) return `כל יום ${at}`;
  if (!c.domStar && c.dowStar) {
    const days = [...c.dom].sort((a, b) => a - b);
    return days.length === 1 ? `ב-${days[0]} לחודש ${at}` : `ב-${days.join(', ')} לחודש ${at}`;
  }
  if (c.domStar && !c.dowStar) {
    const days = [...c.dow].sort((a, b) => a - b);
    if (days.length === 1) return `כל יום ${DAY_NAME[days[0]!]} ${at}`;
    const contiguous = days.every((d, i) => i === 0 || d === days[i - 1]! + 1);
    if (contiguous && days.length >= 3) return `ימים ${DAY_LETTER[days[0]!]}-${DAY_LETTER[days.at(-1)!]} ${at}`;
    return `בימים ${days.map((d) => DAY_LETTER[d]).join(', ')} ${at}`;
  }
  return fallback;
}

/** תאריך ושעה מקומיים קצרים: "16.10 07:00". */
export function whenHe(ms: number): string {
  const l = localParts(ms);
  return `${l.d}.${l.mo} ${pad(l.h)}:${pad(l.mi)}`;
}
